import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent
} from "@mui/material";

import "../admin/Admin.css";
import {emptySponsorInfo, SponsorListItem, TierListItem} from "../../data/sponsorInfo";
import {editSponsor} from "../../services/SponsorService";
import {PartnerListItem} from "../../data/partnerData";
import Grid from "@mui/material/Grid";

interface EditSponsorModalProps {
    isOpen: boolean;
    onClose: () => void;
    sponsor: SponsorListItem | null;
    setSnackbarMessage: (message: string) => void;
    setSnackbarOpen: (open: boolean) => void;
    setDrawerOpen: (open: boolean) => void;
    refreshSponsors: () => Promise<void>;
    partners: PartnerListItem[];
    tiers: TierListItem[];
}

const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const EditSponsorModal: React.FC<EditSponsorModalProps> = ({ isOpen, onClose, sponsor, setSnackbarMessage, setSnackbarOpen, refreshSponsors, setDrawerOpen, partners, tiers}) => {
    const [sponsorData, setSponsorData] = useState<SponsorListItem>(emptySponsorInfo());
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (sponsor) {
            setSponsorData({
                ...sponsor,
                CommitmentDate: sponsor.CommitmentDate ? formatDateForInput(sponsor.CommitmentDate) : "",
            });
        }
    }, [sponsor]);

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
    };

    const validateFields = () => {
        let newErrors: { [key: string]: string } = {};
        let isValid = true;

        if (!sponsorData?.Name.trim()) {
            newErrors.Name = "Sponsor name is required.";
            isValid = false;
        }
        if (!sponsorData?.Link.trim() || !/^https?:\/\/.+\..+$/.test(sponsorData.Link)) {
            newErrors.Link = "Valid link is required.";
            isValid = false;
        }
        if (!sponsorData?.LogoSmall.trim() || !/^https?:\/\/.+\..+$/.test(sponsorData.LogoSmall)) {
            newErrors.LogoSmall = "Valid small logo link is required.";
            isValid = false;
        }
        if (!sponsorData?.PartnerId.trim()) {
            newErrors.PartnerId = "Partner selection is required.";
            isValid = false;
        }
        if (!sponsorData?.SponsorshipTierId.trim()) {
            newErrors.SponsorshipTierId = "Sponsor tier selection is required.";
            isValid = false;
        }
        if (!sponsorData?.CommitmentDate.trim()) {
            newErrors.CommitmentDate = "Commitment date is required.";
            isValid = false;
        }
        if (sponsorData?.DonationAmount <= 0) {
            newErrors.DonationAmount = "Donation amount must be greater than 0.";
            isValid = false;
        }
        if (sponsorData?.SortOrder < 0) {
            newErrors.SortOrder = "Sort order cannot be negative.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
        if (!sponsorData) return;

        const { name, value } = event.target;
        const oldValue = sponsorData[name as keyof SponsorListItem] ?? "undefined";

        console.log(`Updating ${name} from ${oldValue} to ${value}`); // ✅ Logs old and new value

        setSponsorData(prevState => ({
            ...prevState!,
            [name]: value
        }));

        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: ""
        }));
    };


    const handleSubmit = async () => {
        if (!validateFields()) return;
        setLoading(true);
        setErrorMessage(null);

        try {
            await editSponsor(sponsorData!);
            setSnackbarMessage("Sponsor updated successfully");
            setSnackbarOpen(true);
            await refreshSponsors();
            setDrawerOpen(false);
            onClose();
        } catch (error) {
            setErrorMessage("Failed to update sponsor. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-sponsor-modal">
            <Box sx={modalStyle}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>Edit Sponsor</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <TextField fullWidth label="Sponsor Name" name="Name" value={sponsorData?.Name || ""} onChange={handleChange} margin="normal" error={!!errors.Name} helperText={errors.Name} />
                        <TextField fullWidth label="Link" name="Link" value={sponsorData?.Link || ""} onChange={handleChange} margin="normal" error={!!errors.Link} helperText={errors.Link} />
                        <TextField fullWidth label="Logo Small" name="LogoSmall" value={sponsorData?.LogoSmall || ""} onChange={handleChange} margin="normal" error={!!errors.LogoSmall} helperText={errors.LogoSmall} />
                        <TextField fullWidth label="Logo Medium" name="LogoMedium" value={sponsorData?.LogoMedium || ""} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Logo Large" name="LogoLarge" value={sponsorData?.LogoLarge || ""} onChange={handleChange} margin="normal" />
                        <TextField fullWidth label="Logo Banner" name="LogoBanner" value={sponsorData?.LogoBanner || ""} onChange={handleChange} margin="normal" />
                        <TextField
                            fullWidth
                            label="Sort Order"
                            name="SortOrder"
                            type="number"
                            value={sponsorData?.SortOrder ?? 0}  // Ensuring default value
                            onChange={handleChange}
                            margin="normal"
                            error={!!errors.SortOrder}
                            helperText={errors.SortOrder}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="normal" error={!!errors.PartnerId}>
                            <InputLabel id="partner-label">Partner</InputLabel>
                            <Select labelId="partner-label" name="PartnerId" value={sponsorData?.PartnerId || ""} onChange={(e) => handleChange(e as SelectChangeEvent<string>)} label="Partner">
                                <MenuItem value="" disabled>Select a Partner</MenuItem>
                                {partners?.map((partner) => (
                                    <MenuItem key={partner.PartnerId} value={partner.PartnerId}>{partner.Name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal" error={!!errors.SponsorshipTierId}>
                            <InputLabel id="tier-label">Sponsor Tier</InputLabel>
                            <Select labelId="tier-label" name="SponsorshipTierId" value={sponsorData?.SponsorshipTierId || ""} onChange={(e) => handleChange(e as SelectChangeEvent<string>)} label="Sponsor Tier">
                                <MenuItem value="" disabled>Select a Tier</MenuItem>
                                {tiers?.map((tier) => (
                                    <MenuItem key={tier.SponsorshipTierId} value={tier.SponsorshipTierId}>{tier.Name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Commitment Date"
                            name="CommitmentDate"
                            type="date"
                            value={sponsorData?.CommitmentDate || ""}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                            error={!!errors.CommitmentDate}
                            helperText={errors.CommitmentDate}
                        />
                        <TextField
                            fullWidth
                            label="Donation Amount"
                            name="DonationAmount"
                            type="number"
                            value={sponsorData?.DonationAmount ?? 0}  // Ensuring default value
                            onChange={handleChange}
                            margin="normal"
                            error={!!errors.DonationAmount}
                            helperText={errors.DonationAmount}
                        />
                        <TextField fullWidth label="Notes" name="Notes" multiline rows={8} value={sponsorData?.Notes || ""} onChange={handleChange} margin="normal" />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button onClick={onClose} variant="outlined">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" className="editOrAdd" disabled={loading}>{loading ? "Updating..." : "Save Changes"}</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditSponsorModal;
